'use client';

import { createAuthClient } from 'better-auth/react';
import { /* adminClient, */inferAdditionalFields, genericOAuthClient } from 'better-auth/client/plugins';
import type { auth as SERVER_AUTH } from './server';
import { CONFIG } from '~/env';
import type { OryPolis_OAuth2_QueryParams } from './ory-polis/config';
import type { I18N_CONF } from '@funkey/locales/config';
import type { GET as Auth_RedirectHub_GET } from '~/app/api/auth/redirect-hub/route';
import { createContext, use, useMemo } from 'react';
import { _internal_auth_mapSession, GENERIC_OAUTH_PROVIDER_IDS, type Auth_Session } from './shared';
import { std } from '@funkey/shared';
const authClient = createAuthClient({
  baseURL: CONFIG.APP_URL,
  schema: {},
  plugins: [
  // adminClient({}), // https://www.better-auth.com/docs/plugins/admin
  genericOAuthClient(),
  // To signIn with @ory/polis
  inferAdditionalFields<typeof SERVER_AUTH>()]
});

/** Client auth SDK */
export namespace Auth {
  export type Session = Auth_Session;
  type SessionMode = 'required' | 'optional';
  type SessionData<T extends SessionMode> = Auth.Session
  // This check is done in the context provider on the server.
  | (T extends 'required' ? never : { [K in keyof Auth.Session]: null });
  type SessionState<T extends SessionMode> = Readonly<SessionData<T> & {
    isLoading: boolean;
    refetch(): void;
  }>;
  //     Readonly<
  //     // {
  //     //     isLoading: boolean;
  //     //     refetch(): void;
  //     // } &
  //         (
  //         | Auth.Session
  //         | T extends 'required' ? never : {
  //             // This check is done in the context provider on the server.
  //             user: null;
  //             session: null;
  //         })
  // >; // prettier-ignore

  export function useSession(mode: 'required'): SessionState<'required'>;
  export function useSession(mode?: 'optional'): SessionState<'optional'>;
  export function useSession<T extends SessionMode>(mode?: T): SessionState<T> {
    // Custom hook while awaiting https://github.com/better-auth/better-auth/issues/4319

    const ctxSession = use(__Auth_SessionCtx);
    const clientSession = authClient.useSession();
    const session = useMemo<Auth.Session | null>(() => {
      if (mode === 'required' && !ctxSession) {
        // Should not be reachable.
        // If it is reached, you have 2 options:
        // - Make the session context have a required session
        // - Make the `mode` optional
        throw new Error('UNAUTHORIZED');
      }
      if (clientSession.data) return _internal_auth_mapSession(clientSession.data);
      return ctxSession ?? null;
    }, [clientSession.data, ctxSession, mode]);
    return useMemo((): SessionState<'required'> | SessionState<'optional'> => {
      return {
        ...(session ? session : {
          user: null,
          session: null
        }),
        isLoading: clientSession.isPending,
        refetch() {
          return clientSession.refetch();
        }
      };
    }, [session, clientSession.isPending, clientSession.refetch]) as SessionState<T>;
  }
  type SignInConfig = {
    /** Callback URL to redirect the user to on successful signIn */
    callbackURL: false /* Disabled */ | CallbackConfig;
  };
  export namespace signIn {
    export function credentials(data: {
      email: string;
      password: string;
    }, config: SignInConfig) {
      return authClient.signIn.email({
        email: data.email.toLowerCase(),
        password: data.password,
        rememberMe: true,
        callbackURL: config.callbackURL === false ? undefined : CallbackConfig.parse(config.callbackURL)
      });
    }
    export function saml(data: {
      bizzSlug: string;
    }, config: SignInConfig) {
      return authClient.signIn.oauth2({
        providerId: GENERIC_OAUTH_PROVIDER_IDS.ORY_POLIS,
        callbackURL: config.callbackURL === false ? undefined : CallbackConfig.parse(config.callbackURL),
        fetchOptions: {
          query: {
            tenant: data.bizzSlug
          } satisfies OryPolis_OAuth2_QueryParams
        }
      });
    }
  }

  // TODO: idk if this redirects. Maybe convert this to a server action
  export async function signOut(options: {
    redirectTo: string;
  }) {
    const result = await authClient.signOut();
    if (result.error) throw result.error;

    // In client components, `next/navigation` redirect can throw an internal control-flow
    // error. Use browser navigation to avoid surfacing false errors in UI catch blocks.
    window.location.assign(options.redirectTo);
  }
}
const __Auth_SessionCtx = createContext<Auth.Session | null>(null);
export const __Auth_SessionCtx_Provider: React.FC<React.ComponentProps<typeof __Auth_SessionCtx.Provider>> = props => <__Auth_SessionCtx.Provider {...props} />;
namespace CallbackConfig {
  export function parse(config: CallbackConfig) {
    if (typeof config === 'string') return config;

    // Logic is here to ensure no other searchParams get added to the URL
    const sp: Auth_RedirectHub_GET.SearchParams = {
      locale: config.locale
    };
    return `/api/auth/redirect-hub?${new URLSearchParams(std.Object.removeNullish(sp)).toString()}`;
  }
}
type CallbackConfig = string /* Path string */ | {
  locale: I18N_CONF.Locale;
};